import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/auth.service';
import './ViewProfile.css';

const ViewProfile = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  const [profile, setProfile]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [activeTab, setActiveTab]       = useState('profile');
  const [isChangingPwd, setIsChangingPwd] = useState(false);
  const [toast, setToast]               = useState(null);

  const [pwdForm, setPwdForm] = useState({
    currentPassword: '', newPassword: '', confirmPassword: '',
  });
  const [pwdErrors, setPwdErrors] = useState({});

  // ── fetch fresh profile ──────────────────────────────────────────────────
  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await authService.getProfile();
        if (res.success) setProfile(res.data);
      } catch {
        // fall back to context user
        setProfile(user);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── helpers ──────────────────────────────────────────────────────────────
  const getInitials = (p) => {
    if (!p) return '??';
    const first = p.firstName?.[0] || '';
    const last  = p.lastName?.[0]  || '';
    return (first + last).toUpperCase() || p.username?.[0]?.toUpperCase() || '?';
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const getRoleColor = (role) => {
    const map = {
      SUPER_ADMIN:   { bg: '#fce4ec', color: '#c62828' },
      OFFICE_ADMIN:  { bg: '#f3e5f5', color: '#6a1b9a' },
      ADMIN:         { bg: '#e8eaf6', color: '#283593' },
      SALES_OFFICER: { bg: '#e3f2fd', color: '#1565c0' },
      FIELD_AGENT:   { bg: '#e8f5e9', color: '#2e7d32' },
      ACCOUNTS:      { bg: '#fff3e0', color: '#e65100' },
      GODOWN_MANAGER:{ bg: '#fafafa', color: '#424242' },
    };
    return map[role] || { bg: '#f5f5f5', color: '#616161' };
  };

  // ── password change ──────────────────────────────────────────────────────
  const validatePwd = () => {
    const e = {};
    if (!pwdForm.currentPassword)              e.currentPassword = 'Current password is required';
    if (!pwdForm.newPassword)                  e.newPassword     = 'New password is required';
    else if (pwdForm.newPassword.length < 6)   e.newPassword     = 'Minimum 6 characters';
    if (pwdForm.newPassword !== pwdForm.confirmPassword) e.confirmPassword = 'Passwords do not match';
    setPwdErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChangePwd = async (e) => {
    e.preventDefault();
    if (!validatePwd()) return;
    setIsChangingPwd(true);
    try {
      await authService.changePassword({
        currentPassword: pwdForm.currentPassword,
        newPassword:     pwdForm.newPassword,
        confirmPassword: pwdForm.confirmPassword,
      });
      showToast('Password changed successfully!');
      setPwdForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPwdErrors({});
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to change password', 'error');
    } finally {
      setIsChangingPwd(false);
    }
  };

  // ── render ───────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="vp-loading">
      <div className="vp-spinner" />
      <p>Loading profile...</p>
    </div>
  );

  const p          = profile || user;
  const roleStyle  = getRoleColor(p?.role);
  const fullName   = `${p?.firstName || ''} ${p?.lastName || ''}`.trim() || p?.username || '—';
  const displayRole = p?.roleDisplayName || (typeof p?.role === 'string' ? p.role.replace(/_/g, ' ') : '—');

  return (
    <div className="vp-container">

      {/* Toast */}
      {toast && (
        <div className={`vp-toast vp-toast--${toast.type}`}>{toast.message}</div>
      )}

      {/* Breadcrumb */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/dashboard/overview">Dashboard</a></li>
          <li className="breadcrumb-item active">My Profile</li>
        </ol>
      </nav>

      {/* Hero Card */}
      <div className="vp-hero">
        <div className="vp-hero__avatar">
          {p?.avatar
            ? <img src={p.avatar} alt={fullName} className="vp-hero__avatar-img" />
            : <span className="vp-hero__initials">{getInitials(p)}</span>
          }
        </div>
        <div className="vp-hero__info">
          <h2 className="vp-hero__name">{fullName}</h2>
          <p className="vp-hero__username">@{p?.username || '—'}</p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
            <span className="vp-badge" style={{ background: roleStyle.bg, color: roleStyle.color }}>
              {displayRole}
            </span>
            <span className={`vp-badge vp-badge--status ${p?.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
              {p?.status || '—'}
            </span>
          </div>
        </div>
        <div className="vp-hero__meta">
          {p?.employeeCode && (
            <div className="vp-meta-item">
              <span className="vp-meta-label">Employee Code</span>
              <span className="vp-meta-value">{p.employeeCode}</span>
            </div>
          )}
          {p?.companyName && (
            <div className="vp-meta-item">
              <span className="vp-meta-label">Company</span>
              <span className="vp-meta-value">{p.companyName}</span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="vp-tabs">
        {[
          { key: 'profile',  label: '👤 Profile Info' },
          { key: 'security', label: '🔒 Security' },
        ].map(t => (
          <button key={t.key}
            className={`vp-tab ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Profile Info Tab ── */}
      {activeTab === 'profile' && (
        <div className="vp-card">
          <p className="vp-section-title">Personal Information</p>
          <div className="vp-grid">
            <InfoItem label="First Name"    value={p?.firstName} />
            <InfoItem label="Last Name"     value={p?.lastName} />
            <InfoItem label="Username"      value={p?.username} />
            <InfoItem label="Email"         value={p?.email} />
            <InfoItem label="Phone"         value={p?.phone} />
            <InfoItem label="Employee Code" value={p?.employeeCode} />
          </div>

          <p className="vp-section-title" style={{ marginTop: 24 }}>Role & Organization</p>
          <div className="vp-grid">
            <InfoItem label="Role"         value={displayRole} />
            <InfoItem label="Role Code"    value={p?.roleShortCode} />
            <InfoItem label="Company"      value={p?.companyName} />
            <InfoItem label="Reports To"   value={
              p?.reportingManager
                ? `${p.reportingManager.firstName || ''} ${p.reportingManager.lastName || ''}`.trim()
                : p?.reportingTo ? `ID: ${p.reportingTo}` : null
            } />
          </div>

          {/* Account dates if available */}
          {(p?.createdAt || p?.lastLoginAt) && (
            <>
              <p className="vp-section-title" style={{ marginTop: 24 }}>Account Info</p>
              <div className="vp-grid">
                {p?.createdAt    && <InfoItem label="Member Since"  value={formatDate(p.createdAt)} />}
                {p?.lastLoginAt  && <InfoItem label="Last Login"    value={formatDate(p.lastLoginAt)} />}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Security Tab ── */}
      {activeTab === 'security' && (
        <div className="vp-card">
          <p className="vp-section-title">Change Password</p>
          <form onSubmit={handleChangePwd} style={{ maxWidth: 460 }}>

            <div className="vp-form-group">
              <label className="vp-label">Current Password <span className="required">*</span></label>
              <input type="password" className={`vp-input ${pwdErrors.currentPassword ? 'error' : ''}`}
                placeholder="Enter current password"
                value={pwdForm.currentPassword}
                onChange={e => {
                  setPwdForm(p => ({ ...p, currentPassword: e.target.value }));
                  if (pwdErrors.currentPassword) setPwdErrors(p => ({ ...p, currentPassword: '' }));
                }} />
              {pwdErrors.currentPassword && <span className="vp-error">{pwdErrors.currentPassword}</span>}
            </div>

            <div className="vp-form-group">
              <label className="vp-label">New Password <span className="required">*</span></label>
              <input type="password" className={`vp-input ${pwdErrors.newPassword ? 'error' : ''}`}
                placeholder="Min. 6 characters"
                value={pwdForm.newPassword}
                onChange={e => {
                  setPwdForm(p => ({ ...p, newPassword: e.target.value }));
                  if (pwdErrors.newPassword) setPwdErrors(p => ({ ...p, newPassword: '' }));
                }} />
              {pwdErrors.newPassword && <span className="vp-error">{pwdErrors.newPassword}</span>}
            </div>

            <div className="vp-form-group">
              <label className="vp-label">Confirm New Password <span className="required">*</span></label>
              <input type="password" className={`vp-input ${pwdErrors.confirmPassword ? 'error' : ''}`}
                placeholder="Re-enter new password"
                value={pwdForm.confirmPassword}
                onChange={e => {
                  setPwdForm(p => ({ ...p, confirmPassword: e.target.value }));
                  if (pwdErrors.confirmPassword) setPwdErrors(p => ({ ...p, confirmPassword: '' }));
                }} />
              {pwdErrors.confirmPassword && <span className="vp-error">{pwdErrors.confirmPassword}</span>}
            </div>

            <button type="submit" className="vp-btn-primary" disabled={isChangingPwd}>
              {isChangingPwd ? 'Saving...' : '🔒 Update Password'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

// ── small helper component ────────────────────────────────────────────────────
const InfoItem = ({ label, value }) => (
  <div className="vp-info-item">
    <span className="vp-info-label">{label}</span>
    <span className="vp-info-value">{value || '—'}</span>
  </div>
);

export default ViewProfile;